import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AppShell } from "../components/AppShell";
import { Button } from "../components/ui/Button";
import { Card, CardBody } from "../components/ui/Card";
import { Badge } from "../components/ui/Badge";
import { Textarea } from "../components/ui/Textarea";
import { Sheet } from "../components/ui/Sheet";
import { StatusBadge } from "../components/StatusBadge";
import { api, ApiException } from "../lib/api";
import { arriveByIso, durationMinutes, fmtDate, fmtTimeCt } from "../lib/dates";

interface VisitDetailResp {
  visit: {
    id: string;
    circle_id: string;
    title: string | null;
    provider_id: string | null;
    provider_name_snapshot: string | null;
    provider_specialty_snapshot: string | null;
    facility_name_snapshot: string | null;
    address_snapshot: string | null;
    phone_snapshot: string | null;
    visit_category: string | null;
    visit_type: "in_person" | "telehealth";
    starts_at: string;
    ends_at: string | null;
    arrive_lead_minutes: number;
    estimated_travel_minutes: number | null;
    ride_required: number;
    pre_visit_instructions: string | null;
    notes: string | null;
    status: "open" | "accepted" | "cancelled" | "completed";
    primary_acceptor_id: string | null;
    pending_approval?: number;
    created_by_user_id?: string;
  };
  role: "admin" | "member";
  primary_acceptor: { id: string; name: string | null; email: string; phone: string | null } | null;
  assists: Array<{ user_id: string; name: string | null; email: string }>;
  notes: Array<{ id: string; body: string; created_at: string; name: string | null; email: string }>;
}

interface MeResponse {
  user: { id: string } | null;
}

function prettyCategory(c: string | null): string | null {
  if (!c) return null;
  return c.replace(/_/g, " ").replace(/\b\w/g, (s) => s.toUpperCase());
}

export function VisitDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<VisitDetailResp | null>(null);
  const [me, setMe] = useState<MeResponse | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [noteBody, setNoteBody] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  async function refresh() {
    if (!id) return;
    const [meRes, vRes] = await Promise.all([
      api.get<MeResponse>("/api/me"),
      api.get<VisitDetailResp>(`/api/visits/${id}`),
    ]);
    setMe(meRes);
    setData(vRes);
  }

  useEffect(() => {
    refresh().catch((err) => {
      if (err instanceof ApiException && err.status === 401) navigate("/signin", { replace: true });
      if (err instanceof ApiException && err.status === 404) setError("Visit not found.");
    });
  }, [id, navigate]);

  if (!data || !me) {
    return (
      <AppShell activeTab="visits">
        <div className="p-5 text-text-muted">{error ?? "Loading…"}</div>
      </AppShell>
    );
  }

  const v = data.visit;
  const isAdmin = data.role === "admin";
  const isAcceptor = v.primary_acceptor_id === me.user?.id;
  const arriveBy = arriveByIso(v.starts_at, v.arrive_lead_minutes);
  const dur = durationMinutes(v.starts_at, v.ends_at);
  const acceptorFirst = data.primary_acceptor?.name?.split(" ")[0] ?? data.primary_acceptor?.email;

  async function doAccept() {
    setBusy("accept");
    setError(null);
    try {
      await api.post(`/api/visits/${id}/accept`);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiException ? err.message : "Failed.");
    } finally {
      setBusy(null);
    }
  }

  async function doAssist() {
    setBusy("assist");
    setError(null);
    try {
      await api.post(`/api/visits/${id}/assist`);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiException ? err.message : "Failed.");
    } finally {
      setBusy(null);
    }
  }

  async function doDecline(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy("decline");
    setError(null);
    try {
      await api.post(`/api/visits/${id}/decline`, { reason: declineReason });
      setDeclineOpen(false);
      setDeclineReason("");
      setInfo("Decline sent to admins.");
      await refresh();
    } catch (err) {
      setError(err instanceof ApiException ? err.message : "Failed.");
    } finally {
      setBusy(null);
    }
  }

  async function doCancel() {
    if (!confirm("Cancel this visit? Everyone in the circle will be notified.")) return;
    setBusy("cancel");
    setError(null);
    try {
      await api.del(`/api/visits/${id}`);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiException ? err.message : "Failed.");
    } finally {
      setBusy(null);
    }
  }

  async function approveProposal() {
    setBusy("approve");
    setError(null);
    try {
      await api.post(`/api/visits/${id}/approve`);
      setInfo("Approved — the family has been notified.");
      await refresh();
    } catch (err) {
      setError(err instanceof ApiException ? err.message : "Failed.");
    } finally {
      setBusy(null);
    }
  }

  async function rejectProposal() {
    const reason = prompt("Optional: why are you rejecting? (the proposer will see this)");
    if (reason === null) return; // user cancelled the prompt itself
    setBusy("reject");
    setError(null);
    try {
      await api.post(`/api/visits/${id}/reject`, { reason: reason || null });
      navigate("/app/visits", { replace: true });
    } catch (err) {
      setError(err instanceof ApiException ? err.message : "Failed.");
      setBusy(null);
    }
  }

  async function addNote(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy("note");
    setError(null);
    try {
      await api.post(`/api/visits/${id}/notes`, { body: noteBody });
      setNoteBody("");
      await refresh();
    } catch (err) {
      setError(err instanceof ApiException ? err.message : "Failed.");
    } finally {
      setBusy(null);
    }
  }

  const mapsUrl = v.address_snapshot
    ? `https://maps.apple.com/?q=${encodeURIComponent(v.address_snapshot.replace(/\n/g, ", "))}`
    : null;
  const telUrl = v.phone_snapshot ? `tel:${v.phone_snapshot.replace(/[^+\d]/g, "")}` : null;

  return (
    <AppShell activeTab="visits">
      <div className="mx-auto max-w-2xl px-5 py-6 sm:py-8">
        {v.pending_approval ? (
          <div className="mb-4 rounded-lg border border-warning/30 bg-warning-soft p-4">
            <div className="flex items-start gap-3">
              <div className="h-8 w-8 rounded-lg bg-warning/15 text-warning flex items-center justify-center shrink-0 font-bold">!</div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-warning">Pending admin approval</div>
                <p className="mt-1 text-sm text-text-muted">
                  {isAdmin
                    ? "A family member proposed this visit. Review the details below and approve to broadcast it to the family, or reject if something's off."
                    : v.created_by_user_id === me.user?.id
                    ? "You proposed this visit. An admin will review it — once approved, the family is automatically notified."
                    : "This visit was proposed by a family member. It's waiting for an admin to review."}
                </p>
                {isAdmin && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button onClick={approveProposal} loading={busy === "approve"}>Approve & notify family</Button>
                    <Button variant="secondary" onClick={rejectProposal} loading={busy === "reject"} className="text-danger">
                      Reject
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : null}

        <Card>
          <CardBody>
            <div className="flex items-center gap-2 text-sm text-text-muted">
              {v.visit_category && <Badge tone="neutral">{prettyCategory(v.visit_category)}</Badge>}
            </div>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight">
              {v.provider_name_snapshot ?? v.title ?? "Doctor visit"}
            </h1>
            <p className="text-text-muted">{fmtDate(v.starts_at)}</p>
          </CardBody>
        </Card>

        <Card className="mt-4">
          <CardBody>
            <div className="text-lg font-semibold">Arrive by {fmtTimeCt(arriveBy)}</div>
            <div className="text-text-muted text-sm">Starts at {fmtTimeCt(v.starts_at)} · {dur} min</div>
            <div className="mt-3 flex gap-2 flex-wrap">
              <a href={`/api/visits/${v.id}/ics`}>
                <Button variant="secondary">Add to calendar</Button>
              </a>
              {isAdmin && v.status !== "cancelled" && (
                <>
                  <Link to={`/app/visits/${v.id}/edit`}>
                    <Button variant="secondary">Edit</Button>
                  </Link>
                  <Button variant="ghost" onClick={doCancel} loading={busy === "cancel"} className="text-danger">
                    Cancel visit
                  </Button>
                </>
              )}
            </div>
          </CardBody>
        </Card>

        {error && (
          <div className="mt-3 rounded-md bg-red-50 border border-red-200 text-danger text-sm px-3 py-2">{error}</div>
        )}
        {info && (
          <div className="mt-3 rounded-md bg-green-50 border border-green-200 text-success text-sm px-3 py-2">{info}</div>
        )}

        <Card className="mt-4">
          <CardBody>
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm text-text-muted">Status</div>
                <div className="mt-1 flex items-center gap-2">
                  <StatusBadge status={v.status} />
                  {data.primary_acceptor && (
                    <span className="text-sm">Covered by <strong>{data.primary_acceptor.name ?? data.primary_acceptor.email}</strong></span>
                  )}
                </div>
                {data.assists.length > 0 && (
                  <div className="mt-1 text-sm text-text-muted">
                    Assisting: {data.assists.map((a) => a.name ?? a.email).join(", ")}
                  </div>
                )}
              </div>
            </div>
            <div className="mt-4 flex gap-2 flex-wrap">
              {!v.pending_approval && v.status === "open" && (
                <>
                  <Button onClick={doAccept} loading={busy === "accept"}>Accept</Button>
                  <Button variant="secondary" onClick={() => setDeclineOpen(true)}>Decline</Button>
                </>
              )}
              {!v.pending_approval && v.status === "accepted" && !isAcceptor && !data.assists.some((a) => a.user_id === me.user?.id) && (
                <Button onClick={doAssist} loading={busy === "assist"}>Assist {acceptorFirst}</Button>
              )}
              {!v.pending_approval && v.status === "accepted" && isAcceptor && (
                <Badge tone="success">You're covering this visit</Badge>
              )}
            </div>
          </CardBody>
        </Card>

        {(v.provider_name_snapshot || v.facility_name_snapshot || v.address_snapshot || v.phone_snapshot) && (
          <Card className="mt-4">
            <CardBody>
              <div className="font-semibold text-lg">{v.provider_name_snapshot ?? "—"}</div>
              {v.provider_specialty_snapshot && <div className="text-text-muted">{v.provider_specialty_snapshot}</div>}
              {v.facility_name_snapshot && <div className="text-text-muted">{v.facility_name_snapshot}</div>}
              {v.address_snapshot && (
                <div className="mt-2 text-sm whitespace-pre-line">{v.address_snapshot}</div>
              )}
              <div className="mt-3 flex gap-2 flex-wrap">
                {mapsUrl && (
                  <a href={mapsUrl} target="_blank" rel="noreferrer">
                    <Button variant="secondary">Map</Button>
                  </a>
                )}
                {telUrl && (
                  <a href={telUrl}>
                    <Button variant="secondary">Call {v.phone_snapshot}</Button>
                  </a>
                )}
              </div>
            </CardBody>
          </Card>
        )}

        {v.pre_visit_instructions && (
          <Card className="mt-4">
            <CardBody>
              <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wide">Instructions from clinic</h2>
              <p className="mt-2 whitespace-pre-line text-sm">{v.pre_visit_instructions}</p>
            </CardBody>
          </Card>
        )}

        {v.notes && (
          <Card className="mt-4">
            <CardBody>
              <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wide">Family notes</h2>
              <p className="mt-2 whitespace-pre-line text-sm">{v.notes}</p>
            </CardBody>
          </Card>
        )}

        {/* Post-visit notes (acceptor or admin) */}
        {(isAcceptor || isAdmin) && v.status !== "cancelled" && (
          <Card className="mt-4">
            <CardBody>
              <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wide">Post-visit notes</h2>
              {data.notes.length > 0 && (
                <ul className="mt-3 space-y-3">
                  {data.notes.map((n) => (
                    <li key={n.id} className="text-sm">
                      <div className="text-text-muted text-xs">
                        {n.name ?? n.email} · {new Date(n.created_at).toLocaleString()}
                      </div>
                      <div className="whitespace-pre-line mt-0.5">{n.body}</div>
                    </li>
                  ))}
                </ul>
              )}
              <form onSubmit={addNote} className="mt-4">
                <Textarea
                  rows={3}
                  value={noteBody}
                  onChange={(e) => setNoteBody(e.target.value)}
                  placeholder="How did it go? Any follow-up?"
                />
                <div className="mt-2">
                  <Button type="submit" loading={busy === "note"} disabled={!noteBody.trim()}>Save note</Button>
                </div>
              </form>
            </CardBody>
          </Card>
        )}

        <div className="mt-6">
          <Link to="/app/visits" className="text-sm text-primary">← Back to visits</Link>
        </div>

        <Sheet
          open={declineOpen}
          onClose={() => setDeclineOpen(false)}
          title="Decline this visit?"
          description="The admins will see your reason. The visit stays open for someone else to accept."
        >
          <form onSubmit={doDecline} className="space-y-4">
            <div>
              <label htmlFor="reason" className="block text-sm font-medium mb-1.5">Reason</label>
              <Textarea
                id="reason"
                required
                rows={3}
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                placeholder="Work conflict, out of town, etc."
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" loading={busy === "decline"}>Send decline</Button>
              <Button type="button" variant="ghost" onClick={() => setDeclineOpen(false)}>Cancel</Button>
            </div>
          </form>
        </Sheet>
      </div>
    </AppShell>
  );
}
